import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui";
import { useT } from "@/lib/i18n";

const FORMATS = ["pdf", "pdf-light", "png", "png-light", "docx", "md", "txt", "zip"] as const;
const hrefFor = (boardId: string, f: (typeof FORMATS)[number]) =>
  f.endsWith("-light")
    ? `/api/boards/${boardId}/export/${f.replace("-light", "")}?theme=light`
    : `/api/boards/${boardId}/export/${f}`;

/** Header dropdown that downloads the current board in a chosen format. */
export function ExportMenu({ boardId }: { boardId: string }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("pointerdown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <Button variant="ghost" className="px-3 py-1.5 text-xs" onClick={() => setOpen((v) => !v)}>
        {t.exportBoard}
      </Button>
      {open ? (
        <div className="absolute right-0 top-full z-40 mt-1 min-w-56 rounded-md border border-border bg-panel py-1 shadow-panel">
          {FORMATS.map((f) => (
            <a
              key={f}
              href={hrefFor(boardId, f)}
              download
              onClick={() => setOpen(false)}
              className="block px-3 py-1.5 text-sm text-text no-underline hover:bg-panel-2"
            >
              {t.exportFormats[f]}
            </a>
          ))}
          <div className="px-3 pb-1 pt-2 text-[11px] text-dim">{t.exportHint}</div>
        </div>
      ) : null}
    </div>
  );
}
